import { useEffect, useState } from 'react'
import { lumi } from '@/lib/lumi'
import { useNavigate } from 'react-router-dom'
import {Plus, Search, Eye, Edit2, Trash2, FileText, Loader2, ChevronDown, Filter, FileDown, FileCheck2, Clock, Printer, CheckCircle, X, TreePine, MapPin, CalendarCheck, AlertTriangle, Globe, ClipboardList, UserPlus, ChevronRight, ChevronLeft, Sprout, Camera, ImageOff, } from 'lucide-react'
import type { Configuracao, Solicitacao, StatusSolicitacao } from '@/types'
import { gerarPDF } from '@/utils/gerarPDF'
import { gerarParecerTecnico, type DadosParecer } from '@/utils/gerarParecerTecnico'
import { formatarData } from '@/utils/protocolo'
import { STATUS_COLORS, STATUS_LIST } from '@/types'
import toast from 'react-hot-toast'

const BRAND = '#0f3b50'

// ─────────────────────────────────────────────────────────────────────────────
// MODAL: Emitir / Editar Parecer Técnico (reutilizável para novo e edição)
// ─────────────────────────────────────────────────────────────────────────────
interface ModalParecerProps {
  sol: Solicitacao
  loading: boolean
  onClose: () => void
  onConfirm: (dados: DadosParecer) => void
  dadosIniciais?: DadosParecer
  titulo?: string
}

function ModalParecer({ sol, loading, onClose, onConfirm, dadosIniciais, titulo }: ModalParecerProps) {
  const [dataVistoria, setDataVistoria]     = useState(dadosIniciais?.dataVistoria || '')
  const [responsavel, setResponsavel]       = useState(dadosIniciais?.responsavelTecnico || '')
  const [cargo, setCargo]                   = useState(dadosIniciais?.cargoResponsavel || '')
  const [registro, setRegistro]             = useState(dadosIniciais?.registroProfissional || '')
  const [inclinacao, setInclinacao]         = useState(dadosIniciais ? dadosIniciais.condicoes.inclinacao : sol.estadoArvore === 'Risco de Queda')
  const [danoRaizes, setDanoRaizes]         = useState(dadosIniciais ? dadosIniciais.condicoes.danoRaizes : sol.estadoArvore === 'Danificando Estrutura')
  const [interferencia, setInterferencia]   = useState(dadosIniciais ? dadosIniciais.condicoes.interferencia : sol.estadoArvore === 'Danificando Estrutura')
  const [cavidades, setCavidades]           = useState(dadosIniciais ? dadosIniciais.condicoes.cavidades : sol.estadoArvore === 'Doente' || sol.estadoArvore === 'Morta')
  const [riscoQueda, setRiscoQueda]         = useState(dadosIniciais ? dadosIniciais.condicoes.riscoQueda : sol.estadoArvore === 'Risco de Queda' || sol.estadoArvore === 'Morta')
  const [outrosFatores, setOutrosFatores]   = useState(dadosIniciais?.condicoes.outrosFatores || '')
  const [favoravel, setFavoravel]           = useState<boolean | null>(dadosIniciais?.favoravel ?? null)
  const [justificativa, setJustificativa]   = useState(dadosIniciais?.justificativa || '')
  const [parecerFinal, setParecerFinal]     = useState(dadosIniciais?.parecerFinal || '')

  const [erroResp, setErroResp]   = useState(false)
  const [erroConcl, setErroConcl] = useState(false)
  const [erroJust, setErroJust]   = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    let err = false
    if (!responsavel.trim()) { setErroResp(true); err = true }   else setErroResp(false)
    if (favoravel === null)   { setErroConcl(true); err = true }  else setErroConcl(false)
    if (!justificativa.trim()) { setErroJust(true); err = true }  else setErroJust(false)
    if (err) { toast.error('Preencha todos os campos obrigatórios.'); return }
    onConfirm({ dataVistoria, responsavelTecnico: responsavel, cargoResponsavel: cargo, registroProfissional: registro,
      condicoes: { inclinacao, danoRaizes, interferencia, cavidades, riscoQueda, outrosFatores },
      favoravel, justificativa, parecerFinal })
  }

  const Check = ({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) => (
    <label className="flex items-start gap-2.5 cursor-pointer group">
      <button type="button" onClick={() => onChange(!value)}
        className={`mt-0.5 w-4 h-4 rounded flex-shrink-0 border-2 flex items-center justify-center transition ${value ? 'bg-[#0f3b50] border-[#0f3b50]' : 'border-gray-300 bg-white group-hover:border-[#0f3b50]'}`}>
        {value && <span className="text-white text-[9px] font-bold leading-none">✓</span>}
      </button>
      <span className={`text-sm leading-tight ${value ? 'text-gray-900 font-medium' : 'text-gray-600'}`}>{label}</span>
    </label>
  )

  const inp = 'w-full px-3.5 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:border-[#0f3b50] focus:ring-2 focus:ring-[#0f3b50]/10'

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl border border-gray-100 max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white rounded-t-3xl border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#e0f2fe' }}>
              <ClipboardList className="w-5 h-5 text-[#0f766e]" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">{titulo || 'Parecer Técnico'}</h2>
              <p className="text-xs text-gray-400 font-mono">{sol.protocolo} – {sol.nomeCompleto}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-2xl transition"><X className="w-4 h-4" /></button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-6">

          {/* Bloco 1 — Identificação */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-widest text-[#0f3b50] mb-3 flex items-center gap-2">
              <span className="w-5 h-5 rounded-lg bg-[#0f3b50] text-white text-[9px] font-bold flex items-center justify-center">1</span>
              Identificação da Vistoria
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Data da Vistoria</label>
                <input type="date" value={dataVistoria} onChange={e => setDataVistoria(e.target.value)} className={inp} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Responsável Técnico <span className="text-red-500">*</span></label>
                <input value={responsavel} onChange={e => { setResponsavel(e.target.value); if (e.target.value.trim()) setErroResp(false) }}
                  placeholder="Nome completo"
                  className={`${inp} ${erroResp ? 'border-red-400 bg-red-50' : ''}`} />
                {erroResp && <p className="mt-1 text-xs text-red-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Campo obrigatório</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Cargo</label>
                <input value={cargo} onChange={e => setCargo(e.target.value)} placeholder="Ex: Engenheiro Agrônomo..." className={inp} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Registro Profissional</label>
                <input value={registro} onChange={e => setRegistro(e.target.value)} placeholder="Ex: CREA-RN 000000-D" className={inp} />
              </div>
            </div>
          </div>

          {/* Bloco 2 — Condições */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-widest text-[#0f3b50] mb-3 flex items-center gap-2">
              <span className="w-5 h-5 rounded-lg bg-[#0f3b50] text-white text-[9px] font-bold flex items-center justify-center">2</span>
              Condições Observadas na Vistoria
            </h3>
            <div className="bg-gray-50 rounded-2xl border border-gray-100 p-4 space-y-3">
              <Check label="Presença de inclinação acentuada do tronco" value={inclinacao} onChange={setInclinacao} />
              <Check label="Danos estruturais em raízes superficiais" value={danoRaizes} onChange={setDanoRaizes} />
              <Check label="Interferência com edificações, muros ou redes de infraestrutura urbana" value={interferencia} onChange={setInterferencia} />
              <Check label="Presença de cavidades, cupins, fungos ou apodrecimento interno" value={cavidades} onChange={setCavidades} />
              <Check label="Risco potencial de queda de galhos ou do indivíduo arbóreo" value={riscoQueda} onChange={setRiscoQueda} />
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Outros fatores observados</label>
                <input value={outrosFatores} onChange={e => setOutrosFatores(e.target.value)}
                  placeholder="Descreva outros fatores, se houver…" className={`${inp} bg-white`} />
              </div>
            </div>
          </div>

          {/* Bloco 3 — Conclusão */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-widest text-[#0f3b50] mb-3 flex items-center gap-2">
              <span className="w-5 h-5 rounded-lg bg-[#0f3b50] text-white text-[9px] font-bold flex items-center justify-center">3</span>
              Conclusão do Parecer <span className="text-red-500 ml-1">*</span>
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <button type="button" onClick={() => { setFavoravel(true); setErroConcl(false) }}
                className={`py-4 rounded-2xl border-2 flex flex-col items-center gap-1.5 transition font-semibold text-sm ${
                  favoravel === true ? 'bg-green-50 border-green-500 text-green-800 shadow-sm'
                  : erroConcl ? 'border-red-300 bg-red-50/30 text-gray-500'
                  : 'border-gray-200 text-gray-500 hover:border-green-300 hover:bg-green-50/50'}`}>
                <span className={`w-8 h-8 rounded-full flex items-center justify-center text-lg ${favoravel === true ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-400'}`}>✓</span>
                FAVORÁVEL
                <span className="text-xs font-normal opacity-70">à remoção</span>
              </button>
              <button type="button" onClick={() => { setFavoravel(false); setErroConcl(false) }}
                className={`py-4 rounded-2xl border-2 flex flex-col items-center gap-1.5 transition font-semibold text-sm ${
                  favoravel === false ? 'bg-red-50 border-red-500 text-red-800 shadow-sm'
                  : erroConcl ? 'border-red-300 bg-red-50/30 text-gray-500'
                  : 'border-gray-200 text-gray-500 hover:border-red-300 hover:bg-red-50/50'}`}>
                <span className={`w-8 h-8 rounded-full flex items-center justify-center text-lg ${favoravel === false ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-400'}`}>✕</span>
                DESFAVORÁVEL
                <span className="text-xs font-normal opacity-70">à remoção</span>
              </button>
            </div>
            {erroConcl && <p className="mt-2 text-xs text-red-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Selecione Favorável ou Desfavorável</p>}
          </div>

          {/* Bloco 4 — Justificativa */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-widest text-[#0f3b50] mb-3 flex items-center gap-2">
              <span className="w-5 h-5 rounded-lg bg-[#0f3b50] text-white text-[9px] font-bold flex items-center justify-center">4</span>
              Justificativa Técnica <span className="text-red-500 ml-1">*</span>
            </h3>
            <textarea value={justificativa} onChange={e => { setJustificativa(e.target.value); if (e.target.value.trim()) setErroJust(false) }}
              rows={4} placeholder="Descreva a fundamentação técnica que embasa a conclusão deste parecer…"
              className={`w-full px-3.5 py-2.5 rounded-2xl border text-sm focus:outline-none resize-none focus:ring-2 ${erroJust ? 'border-red-400 bg-red-50' : 'border-gray-200 focus:border-[#0f3b50] focus:ring-[#0f3b50]/10'}`} />
            {erroJust && <p className="mt-1 text-xs text-red-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> A justificativa técnica é obrigatória</p>}
            <p className="mt-1.5 text-xs text-gray-400">{justificativa.length} caracteres{justificativa.length > 0 && justificativa.length < 30 ? ' — muito curto, detalhe melhor' : ''}</p>
          </div>

          {/* Bloco 5 — Parecer Final (opcional) */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-widest text-[#0f3b50] mb-1.5 flex items-center gap-2">
              <span className="w-5 h-5 rounded-lg bg-gray-200 text-gray-600 text-[9px] font-bold flex items-center justify-center">5</span>
              Texto do Parecer Final <span className="text-gray-400 font-normal normal-case tracking-normal text-xs">(opcional)</span>
            </h3>
            <textarea value={parecerFinal} onChange={e => setParecerFinal(e.target.value)} rows={3}
              placeholder="Gerado automaticamente se deixado em branco…"
              className="w-full px-3.5 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none resize-none focus:border-[#0f3b50] focus:ring-2 focus:ring-[#0f3b50]/10" />
          </div>

          {/* Ações */}
          <div className="flex gap-3 pt-1 border-t border-gray-100">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-2xl hover:bg-gray-50 transition">
              Cancelar
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 py-2.5 text-white text-sm font-semibold rounded-2xl transition flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ backgroundColor: '#0f766e' }}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardList className="w-4 h-4" />}
              {loading ? 'Gerando PDF…' : 'Salvar e Gerar PDF'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// MODAL: Concluir Processo (dados de replantio — só aparece após parecer favorável)
// ─────────────────────────────────────────────────────────────────────────────
interface DadosConclusao {
  arvoreReplantada: string
  tipoArvoreReplantada: string
  enderecoReplantio: string
  dentroDoPrezo: boolean
  observacao: string
}

interface ModalConcluirProps {
  sol: Solicitacao
  onClose: () => void
  onConfirm: (dados: DadosConclusao) => Promise<void>
  loading: boolean
}

function ModalConcluir({ sol, onClose, onConfirm, loading }: ModalConcluirProps) {
  const [arvore, setArvore]           = useState(sol.arvoreReplantada || '')
  const [tipo, setTipo]               = useState(sol.tipoArvoreReplantada || '')
  const [endereco, setEndereco]       = useState(sol.enderecoReplantio || '')
  const [dentroPrazo, setDentroPrazo] = useState(sol.dentroDoPrezo ?? true)
  const [obs, setObs]                 = useState(sol.observacaoFinalizacao || '')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!arvore.trim()) { toast.error('Informe a árvore plantada.'); return }
    if (!tipo.trim())   { toast.error('Informe o tipo/espécie.'); return }
    if (!endereco.trim()) { toast.error('Informe o endereço do replantio.'); return }
    onConfirm({ arvoreReplantada: arvore, tipoArvoreReplantada: tipo, enderecoReplantio: endereco, dentroDoPrezo: dentroPrazo, observacao: obs })
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg p-6 border border-gray-100 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#d1fae5' }}>
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">Concluir Processo</h2>
              <p className="text-xs text-gray-400 font-mono">{sol.protocolo}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-2xl transition"><X className="w-4 h-4" /></button>
        </div>

        <div className="bg-gray-50 rounded-2xl p-4 mb-5 flex flex-col gap-2">
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <CalendarCheck className="w-3.5 h-3.5 text-gray-400" />
            <span><span className="font-semibold">Abertura:</span> {formatarData(sol.createdAt)}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <TreePine className="w-3.5 h-3.5 text-gray-400" />
            <span><span className="font-semibold">Árvore removida:</span> {sol.tipoArvore}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5"><TreePine className="w-3 h-3 inline mr-1" />Árvore Plantada <span className="text-red-500">*</span></label>
            <input value={arvore} onChange={e => setArvore(e.target.value)} placeholder="Ex: Ipê Amarelo, Aroeira..."
              className="w-full px-3.5 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:border-[#0f3b50] focus:ring-2 focus:ring-[#0f3b50]/10" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Tipo / Espécie <span className="text-red-500">*</span></label>
            <input value={tipo} onChange={e => setTipo(e.target.value)} placeholder="Ex: Nativa, Frutífera..."
              className="w-full px-3.5 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:border-[#0f3b50] focus:ring-2 focus:ring-[#0f3b50]/10" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5"><MapPin className="w-3 h-3 inline mr-1" />Endereço do Replantio <span className="text-red-500">*</span></label>
            <input value={endereco} onChange={e => setEndereco(e.target.value)} placeholder="Rua, número, bairro..."
              className="w-full px-3.5 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:border-[#0f3b50] focus:ring-2 focus:ring-[#0f3b50]/10" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Plantio dentro do prazo de 15 dias? <span className="text-red-500">*</span></label>
            <div className="flex gap-3">
              <button type="button" onClick={() => setDentroPrazo(true)}
                className={`flex-1 py-2.5 rounded-2xl border text-sm font-semibold transition ${dentroPrazo ? 'bg-green-50 border-green-400 text-green-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
                ✓ Sim, dentro do prazo
              </button>
              <button type="button" onClick={() => setDentroPrazo(false)}
                className={`flex-1 py-2.5 rounded-2xl border text-sm font-semibold transition ${!dentroPrazo ? 'bg-amber-50 border-amber-400 text-amber-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
                ✕ Fora do prazo
              </button>
            </div>
            {!dentroPrazo && (
              <div className="mt-2 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-2xl p-3">
                <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700">Plantio fora do prazo sujeito a multa conforme <strong>Lei Municipal nº 426/2018</strong>.</p>
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Observação <span className="text-gray-400 font-normal normal-case">(opcional)</span></label>
            <textarea value={obs} onChange={e => setObs(e.target.value)} rows={3}
              placeholder="Observações sobre a conclusão do processo…"
              className="w-full px-3.5 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none resize-none focus:border-[#0f3b50] focus:ring-2 focus:ring-[#0f3b50]/10" />
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-2xl hover:bg-gray-50 transition">
              Cancelar
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 py-2.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-semibold rounded-2xl transition flex items-center justify-center gap-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              {loading ? 'A concluir…' : 'Confirmar Conclusão'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// MODAL: Fluxo de Finalização em 2 etapas (Parecer → Replantio)
// ─────────────────────────────────────────────────────────────────────────────
interface ModalFinalizarProps {
  sol: Solicitacao
  onClose: () => void
  onFinalizarCompleto: (dados: DadosParecer, conclusao?: DadosConclusao) => Promise<void>
  loadingParecer: boolean
  loadingConclusao: boolean
  cfg: Configuracao | null
}

function ModalFinalizar({ sol, onClose, onFinalizarCompleto, loadingParecer, loadingConclusao, cfg }: ModalFinalizarProps) {
  // Se já tem parecer salvo, começa na etapa 2 (replantio); senão, etapa 1 (parecer)
  const jaTemParecer = !!(sol as any).parecerEmitido && !!(sol as any).parecerDados
  const [etapa, setEtapa] = useState<'parecer' | 'replantio'>(jaTemParecer ? 'replantio' : 'parecer')
  const [dadosParecer, setDadosParecer] = useState<DadosParecer | null>(
    jaTemParecer ? (sol as any).parecerDados as DadosParecer : null
  )

  const handleParecerConfirm = async (dados: DadosParecer) => {
    setDadosParecer(dados)
    if (dados.favoravel === true) {
      setEtapa('replantio')
    } else {
      // Desfavorável → finaliza direto sem replantio
      await onFinalizarCompleto(dados, undefined)
    }
  }

  const handleConcluirConfirm = async (conclusao: DadosConclusao) => {
    if (!dadosParecer) return
    await onFinalizarCompleto(dadosParecer, conclusao)
  }

  if (etapa === 'parecer') {
    return (
      <ModalParecer
        sol={sol}
        loading={loadingParecer}
        onClose={onClose}
        onConfirm={handleParecerConfirm}
        dadosIniciais={(sol as any).parecerDados as DadosParecer | undefined}
        titulo="Finalizar Análise — Emitir Parecer Técnico"
      />
    )
  }

  // Etapa 2 — replantio (só se favorável)
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg border border-gray-100 overflow-hidden">
        {/* Indicador de etapas */}
        <div className="px-6 pt-5 pb-4 border-b border-gray-100">
          <div className="flex items-center gap-2 mb-3">
            <div className="flex items-center gap-1.5">
              <span className="w-6 h-6 rounded-full bg-green-500 text-white text-xs font-bold flex items-center justify-center">✓</span>
              <span className="text-xs font-medium text-green-700">Parecer emitido</span>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-300" />
            <div className="flex items-center gap-1.5">
              <span className="w-6 h-6 rounded-full bg-[#0f3b50] text-white text-xs font-bold flex items-center justify-center">2</span>
              <span className="text-xs font-medium text-[#0f3b50]">Dados de Replantio</span>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-2xl">
            <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
            <p className="text-xs text-green-800 font-medium">
              Parecer <strong>FAVORÁVEL</strong> à remoção. Agora registre os dados do replantio para concluir o processo.
            </p>
          </div>
        </div>
        <div className="max-h-[70vh] overflow-y-auto">
          <ModalConcluir
            sol={sol}
            onClose={onClose}
            onConfirm={handleConcluirConfirm}
            loading={loadingConclusao}
          />
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// MODAL: Registar Replantio (novo)
// ─────────────────────────────────────────────────────────────────────────────
interface ModalReplantioProps {
  sol: Solicitacao
  onClose: () => void
  onSalvar: (dados: DadosReplantio) => Promise<void>
  loading: boolean
}

interface DadosReplantio {
  replantioData: string
  replantioResponsavel: string
  replantioEndereco: string
  replantioTipoArvore: string
  replantioFotos: string[]
}

function ModalReplantio({ sol, onClose, onSalvar, loading }: ModalReplantioProps) {
  const [data, setData]               = useState('')
  const [responsavel, setResponsavel] = useState('')
  const [endereco, setEndereco]       = useState((sol as any).enderecoReplantio || [sol.ruaNome, sol.numero, sol.bairroNome].filter(Boolean).join(', '))
  const [tipoArvore, setTipoArvore]   = useState((sol as any).tipoArvoreReplantada || sol.tipoArvore || '')
  const [fotos, setFotos]             = useState<string[]>((sol as any).replantioFotos || [])
  const [uploadingFoto, setUploadingFoto] = useState(false)
  const [fotoPreview, setFotoPreview]     = useState<string | null>(null)

  const inp = 'w-full px-3.5 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:border-[#0f3b50] focus:ring-2 focus:ring-[#0f3b50]/10'

  const handleFotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return
    if (fotos.length >= 3) { toast.error('Máximo de 3 fotos permitido.'); return }
    const file = files[0]
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Formato inválido. Use JPG, PNG ou WEBP.')
      return
    }
    setUploadingFoto(true)
    try {
      const results = await lumi.tools.file.upload([file])
      const ok = results.find(r => r.fileUrl)
      if (ok?.fileUrl) {
        setFotos(prev => [...prev, ok.fileUrl!])
        toast.success('Foto adicionada!')
      } else {
        toast.error('Erro ao fazer upload da foto.')
      }
    } catch { toast.error('Erro ao fazer upload.') }
    finally { setUploadingFoto(false); e.target.value = '' }
  }

  const removerFoto = (idx: number) => setFotos(prev => prev.filter((_, i) => i !== idx))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!data.trim())        { toast.error('Informe a data do plantio.'); return }
    if (!responsavel.trim()) { toast.error('Informe quem realizou o plantio.'); return }
    if (!endereco.trim())    { toast.error('Informe o endereço do replantio.'); return }
    if (!tipoArvore.trim())  { toast.error('Informe o tipo/espécie da árvore plantada.'); return }
    onSalvar({ replantioData: data, replantioResponsavel: responsavel, replantioEndereco: endereco, replantioTipoArvore: tipoArvore, replantioFotos: fotos })
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4 py-6" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg border border-gray-100 max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white rounded-t-3xl border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#d1fae5' }}>
              <Sprout className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">Registar Replantio</h2>
              <p className="text-xs text-gray-400 font-mono">{sol.protocolo} — {sol.nomeCompleto}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-2xl transition"><X className="w-4 h-4" /></button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
          {/* Bloco info */}
          <div className="bg-green-50 border border-green-100 rounded-2xl p-4 flex items-start gap-3">
            <TreePine className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-green-800">
              <p className="font-semibold mb-0.5">Árvore a ser removida</p>
              <p>{sol.tipoArvore} · {sol.estadoArvore} · Porte: {(sol as any).porte || '—'}</p>
              <p className="text-green-600 mt-0.5">{[sol.ruaNome, sol.numero, sol.bairroNome].filter(Boolean).join(', ')}</p>
            </div>
          </div>

          {/* Campos */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Data do Plantio <span className="text-red-500">*</span></label>
              <input type="date" value={data} onChange={e => setData(e.target.value)} className={inp} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Tipo / Espécie Plantada <span className="text-red-500">*</span></label>
              <input value={tipoArvore} onChange={e => setTipoArvore(e.target.value)} placeholder="Ex: Ipê Amarelo, Aroeira..." className={inp} />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Responsável pelo Plantio <span className="text-red-500">*</span></label>
            <input value={responsavel} onChange={e => setResponsavel(e.target.value)} placeholder="Nome de quem realizou o plantio" className={inp} />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Endereço do Replantio <span className="text-red-500">*</span></label>
            <input value={endereco} onChange={e => setEndereco(e.target.value)} placeholder="Rua, número, bairro..." className={inp} />
          </div>

          {/* Upload de fotos */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Fotos do Replantio <span className="text-gray-400 font-normal normal-case">(opcional, máx. 3)</span></label>
              <span className="text-xs text-gray-400">{fotos.length}/3</span>
            </div>

            {/* Galeria de fotos */}
            {fotos.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mb-3">
                {fotos.map((url, idx) => (
                  <div key={idx} className="relative group rounded-2xl overflow-hidden border border-gray-100" style={{ aspectRatio: '1/1' }}>
                    <img src={url} alt={`Foto ${idx + 1}`} className="w-full h-full object-cover" onClick={() => setFotoPreview(url)} />
                    <button type="button" onClick={() => removerFoto(idx)}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500/90 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition text-xs">
                      ✕
                    </button>
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition cursor-pointer" onClick={() => setFotoPreview(url)} />
                  </div>
                ))}
              </div>
            )}

            {fotos.length < 3 && (
              <label className={`flex flex-col items-center justify-center gap-2 w-full py-5 border-2 border-dashed rounded-2xl cursor-pointer transition ${
                uploadingFoto ? 'border-gray-200 bg-gray-50 cursor-not-allowed' : 'border-green-200 hover:border-green-400 hover:bg-green-50/40'
              }`}>
                <input type="file" accept="image/jpeg,image/jpg,image/png,image/webp" onChange={handleFotoUpload} disabled={uploadingFoto} className="hidden" />
                {uploadingFoto
                  ? <Loader2 className="w-5 h-5 text-green-500 animate-spin" />
                  : <Camera className="w-5 h-5 text-green-500" />
                }
                <span className="text-xs text-gray-500">{uploadingFoto ? 'A carregar…' : 'Clique para adicionar foto'}</span>
                <span className="text-[10px] text-gray-400">JPG, PNG ou WEBP</span>
              </label>
            )}
          </div>

          {/* Acções */}
          <div className="flex gap-3 pt-1 border-t border-gray-100">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-2xl hover:bg-gray-50 transition">Cancelar</button>
            <button type="submit" disabled={loading}
              className="flex-1 py-2.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-semibold rounded-2xl transition flex items-center justify-center gap-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sprout className="w-4 h-4" />}
              {loading ? 'A guardar…' : 'Guardar Replantio'}
            </button>
          </div>
        </form>
      </div>

      {/* Lightbox preview */}
      {fotoPreview && (
        <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center" onClick={() => setFotoPreview(null)}>
          <img src={fotoPreview} alt="Preview" className="max-w-[90vw] max-h-[85vh] rounded-2xl shadow-2xl" />
          <button className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white" onClick={() => setFotoPreview(null)}>✕</button>
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// PÁGINA PRINCIPAL
// ─────────────────────────────────────────────────────────────────────────────
export default function SolicitacoesPage() {
  const navigate = useNavigate()
  const [solicitacoes, setSolicitacoes]           = useState<Solicitacao[]>([])
  const [loading, setLoading]                     = useState(true)
  const [search, setSearch]                       = useState('')
  const [filtroStatus, setFiltroStatus]           = useState<string>('')
  const [filtroBairro, setFiltroBairro]           = useState<string>('')
  const [confirmDelete, setConfirmDelete]         = useState<Solicitacao | null>(null)
  const [showFilters, setShowFilters]             = useState(false)
  const [pdfLoading, setPdfLoading]               = useState<string | null>(null)
  const [aba, setAba]                             = useState<'ativas' | 'finalizadas'>('ativas')
  const [finalizarSol, setFinalizarSol]           = useState<Solicitacao | null>(null)
  const [loadingParecerFinal, setLoadingParecerFinal] = useState(false)
  const [loadingConclusao, setLoadingConclusao]   = useState(false)
  const [parecerLoading, setParecerLoading]       = useState<string | null>(null)
  const [parecerSol, setParecerSol]               = useState<Solicitacao | null>(null)
  const [escolherParecerSol, setEscolherParecerSol] = useState<Solicitacao | null>(null)
  const [acessoSol, setAcessoSol]                 = useState<Solicitacao | null>(null)
  const [criandoAcesso, setCriandoAcesso]         = useState(false)
  const [cfg, setCfg]                             = useState<Configuracao | null>(null)
  const [replantioSol, setReplantioSol]           = useState<Solicitacao | null>(null)
  const [loadingReplantio, setLoadingReplantio]   = useState(false)

  const fetchSolicitacoes = () => {
    lumi.entities.solicitacoes.list({ sort: { createdAt: -1 }, limit: 500 }).then((r) => {
      setSolicitacoes((r.list || []) as Solicitacao[])
      setLoading(false)
    }).catch(() => setLoading(false))
  }

  useEffect(() => {
    fetchSolicitacoes()
    lumi.entities.configuracoes.list({ limit: 1 }).then(r => setCfg((r.list?.[0] as Configuracao) || null)).catch(() => {})
  }, [])

  const bairrosUnicos = [...new Set(solicitacoes.map(s => s.bairroNome))].sort()
  const todas = solicitacoes.filter(s => {
    const q = search.toLowerCase()
    const matchSearch = !q || s.protocolo?.toLowerCase().includes(q) || s.nomeCompleto?.toLowerCase().includes(q) || s.cpf?.toLowerCase().includes(q) || s.bairroNome?.toLowerCase().includes(q)
    return matchSearch && (!filtroStatus || s.status === filtroStatus) && (!filtroBairro || s.bairroNome === filtroBairro)
  })

  const onlineCount = solicitacoes.filter((s: any) => s.origem === 'online').length
  const ativas      = todas.filter(s => !s.finalizado)
  const finalizadas = todas.filter(s => !!s.finalizado)
  const filtered    = aba === 'ativas' ? ativas : finalizadas

  // ── Clique no botão Parecer ──────────────────────────────────────────────
  const handleClicarParecer = (s: Solicitacao) => {
    if ((s as any).parecerEmitido) setEscolherParecerSol(s)
    else setParecerSol(s)
  }

  // ── Gerar parecer independente (fora do fluxo de finalização) ────────────
  const handleGerarParecer = async (s: Solicitacao, dados: DadosParecer) => {
    setParecerLoading(s._id)
    try {
      await gerarParecerTecnico(s, cfg, dados)
      const now = new Date().toISOString()
      try {
        await lumi.entities.solicitacoes.update(s._id, { parecerEmitido: true, parecerData: now, parecerDados: dados as any, updatedAt: now })
        setSolicitacoes(prev => prev.map(item => item._id === s._id ? { ...item, parecerEmitido: true, parecerData: now, parecerDados: dados } as any : item))
      } catch (e) { console.error('[GEOA] Erro ao salvar dados do parecer:', e); toast.error('Parecer gerado, mas erro ao salvar no banco.') }
      toast.success('Parecer técnico gerado!')
      setParecerSol(null)
      fetchSolicitacoes()
    } catch { toast.error('Erro ao gerar parecer técnico.') }
    finally { setParecerLoading(null) }
  }

  // ── 2ª via do parecer ────────────────────────────────────────────────────
  const handleSegundaVia = async (s: Solicitacao) => {
    setParecerLoading(s._id)
    try {
      await gerarParecerTecnico(s, cfg, (s as any).parecerDados as DadosParecer)
      toast.success('Segunda via do parecer gerada!')
      setEscolherParecerSol(null)
    } catch { toast.error('Erro ao gerar segunda via.') }
    finally { setParecerLoading(null) }
  }

  // ── Fluxo completo de finalização (Parecer + Replantio) ─────────────────
  const handleFinalizarCompleto = async (dados: DadosParecer, conclusao?: DadosConclusao) => {
    if (!finalizarSol) return
    const s = finalizarSol
    const now = new Date().toISOString()
    const isFavoravel = dados.favoravel === true

    // 1. Gera o PDF do parecer
    setLoadingParecerFinal(true)
    try {
      await gerarParecerTecnico(s, cfg, dados)
    } catch { toast.error('Erro ao gerar PDF do parecer.'); setLoadingParecerFinal(false); return }

    // 2. Determina o novo status
    const novoStatus: StatusSolicitacao = isFavoravel
      ? (conclusao ? 'Processo Concluído' : 'Aprovada')
      : 'Reprovada'

    // 3. Monta payload de update
    const updatePayload: Record<string, any> = {
      parecerEmitido: true,
      parecerData: now,
      parecerDados: dados,
      status: novoStatus,
      updatedAt: now,
    }
    if (isFavoravel && conclusao) {
      updatePayload.finalizado          = true
      updatePayload.dataFinalizacao     = now
      updatePayload.arvoreReplantada    = conclusao.arvoreReplantada
      updatePayload.tipoArvoreReplantada = conclusao.tipoArvoreReplantada
      updatePayload.enderecoReplantio   = conclusao.enderecoReplantio
      updatePayload.dentroDoPrezo       = conclusao.dentroDoPrezo
      updatePayload.observacaoFinalizacao = conclusao.observacao
    }
    if (!isFavoravel) {
      updatePayload.finalizado      = true
      updatePayload.dataFinalizacao = now
    }

    setLoadingConclusao(true)
    try {
      await lumi.entities.solicitacoes.update(s._id, updatePayload)

      // 4. Registra histórico
      const obsHist = isFavoravel
        ? `Parecer FAVORÁVEL. Responsável: ${dados.responsavelTecnico}.${conclusao ? ` Replantio: ${conclusao.arvoreReplantada} (${conclusao.tipoArvoreReplantada}) — ${conclusao.enderecoReplantio}. ${conclusao.dentroDoPrezo ? 'Dentro do prazo.' : 'FORA DO PRAZO.'}` : ''}`
        : `Parecer DESFAVORÁVEL. Responsável: ${dados.responsavelTecnico}. Motivo: ${dados.justificativa.slice(0, 120)}…`

      await lumi.entities.historico_status.create({
        solicitacaoId: s._id,
        protocolo: s.protocolo,
        status: novoStatus,
        responsavel: dados.responsavelTecnico || 'Sistema',
        observacoes: obsHist,
        creator: 'sistema',
        createdAt: now,
        updatedAt: now,
      })

      toast.success(isFavoravel ? 'Processo concluído com sucesso!' : 'Processo encerrado com parecer desfavorável.')
      setFinalizarSol(null)
      fetchSolicitacoes()
    } catch { toast.error('Erro ao finalizar processo.') }
    finally { setLoadingParecerFinal(false); setLoadingConclusao(false) }
  }

  // ── Registar Replantio ───────────────────────────────────────────────────
  const handleSalvarReplantio = async (dados: DadosReplantio) => {
    if (!replantioSol) return
    setLoadingReplantio(true)
    try {
      const now = new Date().toISOString()
      await lumi.entities.solicitacoes.update(replantioSol._id, {
        replantioRegistado: true,
        replantioData: dados.replantioData,
        replantioResponsavel: dados.replantioResponsavel,
        replantioEndereco: dados.replantioEndereco,
        replantioTipoArvore: dados.replantioTipoArvore,
        replantioFotos: dados.replantioFotos,
        updatedAt: now,
      })
      await lumi.entities.historico_status.create({
        solicitacaoId: replantioSol._id,
        protocolo: replantioSol.protocolo,
        status: replantioSol.status,
        responsavel: dados.replantioResponsavel,
        observacoes: `Replantio registado: ${dados.replantioTipoArvore} em ${dados.replantioEndereco} — plantado por ${dados.replantioResponsavel} em ${dados.replantioData}.`,
        creator: 'sistema',
        createdAt: now,
        updatedAt: now,
      })
      toast.success('Replantio registado com sucesso!')
      setReplantioSol(null)
      fetchSolicitacoes()
    } catch { toast.error('Erro ao guardar replantio.') }
    finally { setLoadingReplantio(false) }
  }

  // ── PDF / Imprimir ───────────────────────────────────────────────────────
  const handleGerarPDF = async (s: Solicitacao) => {
    setPdfLoading(s._id)
    try {
      const histRes = await lumi.entities.historico_status.list({ filter: { solicitacaoId: s._id }, sort: { createdAt: -1 }, limit: 50 })
      await gerarPDF(s, (histRes.list || []) as any[], cfg)
      toast.success('PDF gerado!')
    } catch { toast.error('Erro ao gerar PDF.') }
    finally { setPdfLoading(null) }
  }

  const handleImprimir = async (s: Solicitacao) => {
    setPdfLoading('print-' + s._id)
    try {
      const histRes = await lumi.entities.historico_status.list({ filter: { solicitacaoId: s._id }, sort: { createdAt: -1 }, limit: 50 })
      await gerarPDF(s, (histRes.list || []) as any[], cfg)
      toast.success('Documento pronto!')
    } catch { toast.error('Erro ao preparar impressão.') }
    finally { setPdfLoading(null) }
  }

  // ── Excluir ──────────────────────────────────────────────────────────────
  const handleDelete = async (s: Solicitacao) => {
    try {
      await lumi.entities.solicitacoes.delete(s._id)
      toast.success('Solicitação excluída!')
      setConfirmDelete(null)
      fetchSolicitacoes()
    } catch { toast.error('Erro ao excluir.') }
  }

  // ── Criar Acesso ao Portal (admin) ───────────────────────────────────────
  const handleCriarAcessoAdmin = async (s: Solicitacao) => {
    setCriandoAcesso(true)
    try {
      const { normalizarCPF, senhaDosCPF } = await import('@/utils/cidadaoAuth')
      const cpfLimpo = normalizarCPF(s.cpf || '')
      if (!cpfLimpo) { toast.error('CPF não encontrado.'); return }
      const { list: lp } = await lumi.entities.cidadaos_portal.list({ filter: { cpf: cpfLimpo }, limit: 1 })
      const jaExiste = !!(lp && lp.length > 0)
      if (!jaExiste) {
        await lumi.entities.cidadaos_portal.create({ cpf: cpfLimpo, nomeCompleto: s.nomeCompleto, ativo: true, creator: 'admin', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() })
      }
      const emailC = `${cpfLimpo}@cidadao.geoa`
      const { list: lu } = await lumi.entities.usuarios_sistema.list({ filter: { email: emailC }, limit: 1 })
      if (!(lu && lu.length > 0)) {
        const senha = senhaDosCPF(cpfLimpo)
        await lumi.entities.usuarios_sistema.create({ userId: `cidadao-${cpfLimpo}`, userName: s.nomeCompleto, email: emailC, senha, senhaAtual: senha, cargo: 'Cidadão', userRole: 'CIDADAO', ativo: true, creator: 'admin', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() })
      }
      toast.success(jaExiste ? `Acesso já existia para ${s.nomeCompleto}.` : `Acesso criado! Login: ${cpfLimpo}`)
      setAcessoSol(null)
    } catch (err: unknown) {
      toast.error(`Erro: ${err instanceof Error ? err.message : 'Erro desconhecido'}`)
    } finally { setCriandoAcesso(false) }
  }

  const filtersActive = showFilters || !!filtroStatus || !!filtroBairro

  return (
    <div className="space-y-5">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Solicitações</h1>
          <div className="flex items-center gap-3 mt-0.5">
            <p className="text-sm text-gray-500">{todas.length} registro(s) total</p>
            {onlineCount > 0 && (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 font-medium">
                <Globe className="w-3 h-3" />{onlineCount} online
              </span>
            )}
          </div>
        </div>
        <button onClick={() => navigate('/solicitacoes/nova')}
          className="flex items-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-2xl transition"
          style={{ backgroundColor: BRAND }}
          onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#0a2d3d'}
          onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.backgroundColor = BRAND}>
          <Plus className="w-4 h-4" /><span className="hidden sm:inline">Nova Solicitação</span>
        </button>
      </div>

      {/* Abas */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-2xl w-fit">
        <button onClick={() => setAba('ativas')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl transition ${aba === 'ativas' ? 'bg-white text-[#0f3b50] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
          <Clock className="w-4 h-4" />Em Curso
          <span className={`text-xs px-1.5 py-0.5 rounded-lg font-bold ${aba === 'ativas' ? 'bg-[#0f3b50] text-white' : 'bg-gray-200 text-gray-600'}`}>{ativas.length}</span>
        </button>
        <button onClick={() => setAba('finalizadas')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl transition ${aba === 'finalizadas' ? 'bg-white text-green-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
          <FileCheck2 className="w-4 h-4" />Finalizados
          <span className={`text-xs px-1.5 py-0.5 rounded-lg font-bold ${aba === 'finalizadas' ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-600'}`}>{finalizadas.length}</span>
        </button>
      </div>

      {/* Busca e Filtros */}
      <div className="bg-white rounded-3xl border border-gray-100 p-4 shadow-sm space-y-3">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Protocolo, nome, CPF, bairro..."
              className="w-full pl-9 pr-4 py-2 rounded-2xl border border-gray-200 text-sm focus:outline-none"
              onFocus={e => { e.currentTarget.style.borderColor = BRAND; e.currentTarget.style.boxShadow = `0 0 0 2px ${BRAND}20` }}
              onBlur={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.boxShadow = '' }} />
          </div>
          <button onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-1.5 px-3 py-2 border rounded-2xl text-sm transition"
            style={filtersActive ? { borderColor: BRAND, color: BRAND, backgroundColor: '#e8f1f5' } : { borderColor: '#e5e7eb', color: '#4b5563' }}>
            <Filter className="w-4 h-4" /><span className="hidden sm:inline">Filtros</span>
            <ChevronDown className={`w-3 h-3 transition ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>
        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1 border-t border-gray-100">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
              <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}
                className="w-full px-3 py-2 rounded-2xl border border-gray-200 text-sm focus:outline-none bg-white">
                <option value="">Todos os status</option>
                {STATUS_LIST.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Bairro</label>
              <select value={filtroBairro} onChange={e => setFiltroBairro(e.target.value)}
                className="w-full px-3 py-2 rounded-2xl border border-gray-200 text-sm focus:outline-none bg-white">
                <option value="">Todos os bairros</option>
                {bairrosUnicos.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Lista */}
      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 animate-spin mr-2" style={{ color: BRAND }} />
            <span className="text-sm text-gray-500">Carregando...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-14">
            <FileText className="w-9 h-9 text-gray-200 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-400">
              {aba === 'finalizadas' ? 'Nenhum processo finalizado ainda.' : 'Nenhuma solicitação em curso.'}
            </p>
            {aba === 'ativas' && (
              <button onClick={() => navigate('/solicitacoes/nova')}
                className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 text-white text-sm font-medium rounded-2xl transition"
                style={{ backgroundColor: BRAND }}>
                <Plus className="w-4 h-4" /> Nova Solicitação
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {/* Cabeçalho desktop */}
            <div className="hidden md:grid grid-cols-[1fr_1fr_auto_auto_auto] gap-4 items-center px-5 py-3"
              style={{ backgroundColor: '#eef4f7' }}>
              <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: BRAND }}>Protocolo / Solicitante</span>
              <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: BRAND }}>Localização</span>
              <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: BRAND }}>Data</span>
              <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: BRAND }}>Status</span>
              <span className="text-[10px] font-bold uppercase tracking-wide text-right" style={{ color: BRAND }}>Ações</span>
            </div>

            {filtered.map((s, i) => {
              const cls          = STATUS_COLORS[s.status] || 'bg-gray-50 text-gray-700 border-gray-200'
              const isPrinting   = pdfLoading === 'print-' + s._id
              const isDownloading = pdfLoading === s._id
              const temParecer   = !!(s as any).parecerEmitido

              return (
                <div key={s._id} className="px-5 py-4 hover:bg-blue-50/20 transition"
                  style={i % 2 !== 0 ? { backgroundColor: '#fafbfc' } : {}}>

                  {/* Mobile */}
                  <div className="flex flex-col gap-3 md:hidden">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                          <span className="text-xs font-mono font-bold text-gray-900">{s.protocolo}</span>
                          {s.finalizado && <FileCheck2 className="w-3 h-3 text-green-600" />}
                          {(s as any).origem === 'online' && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 font-semibold">
                              <Globe className="w-2.5 h-2.5" />Online
                            </span>
                          )}
                          {temParecer && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200 font-semibold">
                              <ClipboardList className="w-2.5 h-2.5" />Parecer
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-700 font-medium">{s.nomeCompleto}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{s.bairroNome} · {formatarData(s.createdAt)}</p>
                      </div>
                      <span className={`text-[10px] px-2 py-1 rounded-full border font-semibold whitespace-nowrap flex-shrink-0 ${cls}`}>{s.status}</span>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Btn icon={Eye} label="Ver" onClick={() => navigate(`/solicitacoes/${s._id}`)} color={BRAND} />
                      <Btn icon={Edit2} label="Editar" onClick={() => navigate(`/solicitacoes/${s._id}/editar`)} color="#2563eb" />
                      <Btn icon={Printer} label="Imprimir" onClick={() => handleImprimir(s)} loading={isPrinting} color="#4f46e5" />
                      <Btn icon={FileDown} label="PDF" onClick={() => handleGerarPDF(s)} loading={isDownloading} color="#7c3aed" />
                      <Btn icon={ClipboardList} label={temParecer ? 'Parecer (emitido)' : 'Emitir Parecer'} onClick={() => handleClicarParecer(s)} loading={parecerLoading === s._id} color={temParecer ? '#16a34a' : '#0f766e'} />
                      <Btn icon={Sprout} label={(s as any).replantioRegistado ? 'Replantio Registado' : 'Registar Replantio'} onClick={() => setReplantioSol(s)} color={(s as any).replantioRegistado ? '#16a34a' : '#15803d'} />
                      {(s as any).origem === 'online' && <Btn icon={UserPlus} label="Criar Acesso ao Portal" onClick={() => setAcessoSol(s)} color="#7c3aed" />}
                      {!s.finalizado && <Btn icon={CheckCircle} label="Finalizar Análise" onClick={() => setFinalizarSol(s)} color="#16a34a" variant="filled" />}
                      <Btn icon={Trash2} label="Excluir" onClick={() => setConfirmDelete(s)} color="#dc2626" />
                    </div>
                  </div>

                  {/* Desktop */}
                  <div className="hidden md:grid grid-cols-[1fr_1fr_auto_auto_auto] gap-4 items-center">
                    <div>
                      <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                        <span className="text-xs font-mono font-bold text-gray-900">{s.protocolo}</span>
                        {s.finalizado && <FileCheck2 className="w-3 h-3 text-green-600" />}
                        {(s as any).origem === 'online' && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 font-semibold">
                            <Globe className="w-2.5 h-2.5" />Online
                          </span>
                        )}
                        {temParecer && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200 font-semibold">
                            <ClipboardList className="w-2.5 h-2.5" />Parecer emitido
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 truncate max-w-[220px]">{s.nomeCompleto}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-700 truncate">{s.bairroNome}</p>
                      {(s as any).ruaNome && <p className="text-xs text-gray-400 truncate">{(s as any).ruaNome}</p>}
                    </div>
                    <div className="text-sm text-gray-500 whitespace-nowrap">
                      {aba === 'finalizadas' && s.dataFinalizacao ? formatarData(s.dataFinalizacao) : formatarData(s.createdAt)}
                    </div>
                    <div><span className={`text-xs px-2.5 py-1 rounded-full border font-semibold whitespace-nowrap ${cls}`}>{s.status}</span></div>
                    <div className="flex items-center gap-1 justify-end">
                      <Btn icon={Eye} label="Visualizar" onClick={() => navigate(`/solicitacoes/${s._id}`)} color={BRAND} iconOnly />
                      <Btn icon={Edit2} label="Editar" onClick={() => navigate(`/solicitacoes/${s._id}/editar`)} color="#2563eb" iconOnly />
                      <Btn icon={Printer} label="Imprimir" onClick={() => handleImprimir(s)} loading={isPrinting} color="#4f46e5" iconOnly />
                      <Btn icon={FileDown} label="Baixar PDF" onClick={() => handleGerarPDF(s)} loading={isDownloading} color="#7c3aed" iconOnly />
                      <Btn icon={ClipboardList} label={temParecer ? 'Parecer já emitido — Novo ou 2ª Via' : 'Emitir Parecer Técnico'} onClick={() => handleClicarParecer(s)} loading={parecerLoading === s._id} color={temParecer ? '#16a34a' : '#0f766e'} iconOnly />
                      <Btn icon={Sprout} label={(s as any).replantioRegistado ? 'Replantio já registado — Editar' : 'Registar Replantio'} onClick={() => setReplantioSol(s)} color={(s as any).replantioRegistado ? '#16a34a' : '#15803d'} iconOnly />
                      {(s as any).origem === 'online' && <Btn icon={UserPlus} label="Criar Acesso ao Portal do Cidadão" onClick={() => setAcessoSol(s)} color="#7c3aed" iconOnly />}
                      {!s.finalizado ? (
                        <button onClick={() => setFinalizarSol(s)} title="Finalizar Análise / Emitir Parecer"
                          className="flex items-center gap-1 px-2.5 py-1.5 bg-[#0f3b50] hover:bg-[#0a2d3d] text-white text-xs font-semibold rounded-xl transition">
                          <ClipboardList className="w-3.5 h-3.5" /><span>Finalizar</span>
                        </button>
                      ) : (
                        <span className="flex items-center gap-1 px-2.5 py-1.5 bg-green-50 text-green-700 border border-green-200 text-xs font-semibold rounded-xl">
                          <FileCheck2 className="w-3.5 h-3.5" />Concluído
                        </span>
                      )}
                      <Btn icon={Trash2} label="Excluir" onClick={() => setConfirmDelete(s)} color="#dc2626" iconOnly />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Modal Registar Replantio */}
      {replantioSol && (
        <ModalReplantio
          sol={replantioSol}
          onClose={() => setReplantioSol(null)}
          onSalvar={handleSalvarReplantio}
          loading={loadingReplantio}
        />
      )}

      {/* Modal de Finalização (Parecer + Replantio) */}
      {finalizarSol && (
        <ModalFinalizar
          sol={finalizarSol}
          onClose={() => setFinalizarSol(null)}
          onFinalizarCompleto={handleFinalizarCompleto}
          loadingParecer={loadingParecerFinal}
          loadingConclusao={loadingConclusao}
          cfg={cfg}
        />
      )}

      {/* Modal Parecer independente (fora do fluxo de finalização) */}
      {parecerSol && (
        <ModalParecer
          sol={parecerSol}
          loading={parecerLoading === parecerSol._id}
          onClose={() => setParecerSol(null)}
          onConfirm={(dados) => handleGerarParecer(parecerSol, dados)}
          dadosIniciais={(parecerSol as any).parecerDados as DadosParecer | undefined}
        />
      )}

      {/* Modal de escolha: Novo Parecer ou 2ª Via */}
      {escolherParecerSol && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#dcfce7' }}>
                  <ClipboardList className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900">Parecer Técnico</h2>
                  <p className="text-xs text-gray-400 font-mono">{escolherParecerSol.protocolo}</p>
                </div>
              </div>
              <button onClick={() => setEscolherParecerSol(null)} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-2xl transition"><X className="w-4 h-4" /></button>
            </div>

            <div className="bg-green-50 border border-green-100 rounded-2xl p-4 mb-5 flex items-start gap-2.5">
              <FileCheck2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-700">
                Esta solicitação já possui um parecer técnico emitido
                {(escolherParecerSol as any).parecerData ? (
                  <span className="font-semibold"> em {formatarData((escolherParecerSol as any).parecerData)}</span>
                ) : null}. O que deseja fazer?
              </p>
            </div>

            <div className="space-y-3">
              <button onClick={() => handleSegundaVia(escolherParecerSol)} disabled={parecerLoading === escolherParecerSol._id}
                className="w-full flex items-center gap-3 p-4 rounded-2xl border-2 border-green-200 bg-green-50/50 hover:bg-green-50 hover:border-green-400 transition text-left disabled:opacity-50">
                <div className="w-9 h-9 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                  {parecerLoading === escolherParecerSol._id ? <Loader2 className="w-4 h-4 text-green-700 animate-spin" /> : <FileDown className="w-4 h-4 text-green-700" />}
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Emitir 2ª via do parecer</p>
                  <p className="text-xs text-gray-500">Reimprime o parecer já emitido, com os mesmos dados.</p>
                </div>
              </button>

              <button onClick={() => { const s = escolherParecerSol; setEscolherParecerSol(null); setParecerSol(s) }}
                className="w-full flex items-center gap-3 p-4 rounded-2xl border-2 border-gray-200 hover:border-[#0f766e] hover:bg-[#0f766e]/5 transition text-left">
                <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <Edit2 className="w-4 h-4 text-[#0f766e]" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Editar / Emitir novo parecer</p>
                  <p className="text-xs text-gray-500">Abre o formulário pré-preenchido com os dados anteriores para edição.</p>
                </div>
              </button>
            </div>

            <button onClick={() => setEscolherParecerSol(null)}
              className="mt-4 w-full py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-2xl hover:bg-gray-50 transition">
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Modal Excluir */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-3xl shadow-xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-2xl bg-red-50 flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">Excluir Solicitação</h2>
                <p className="text-xs text-gray-400 font-mono">{confirmDelete.protocolo}</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-5">Esta ação é irreversível. O processo e todo o histórico serão removidos permanentemente.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-2xl hover:bg-gray-50 transition">
                Cancelar
              </button>
              <button onClick={() => handleDelete(confirmDelete)}
                className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-2xl transition">
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Criar Acesso Portal */}
      {acessoSol && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#f3e8ff' }}>
                  <UserPlus className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900">Criar Acesso ao Portal</h2>
                  <p className="text-xs text-gray-400 font-mono">{acessoSol.protocolo}</p>
                </div>
              </div>
              <button onClick={() => setAcessoSol(null)} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-2xl transition"><X className="w-4 h-4" /></button>
            </div>
            <div className="bg-purple-50 border border-purple-100 rounded-2xl p-4 mb-5 space-y-2">
              <p className="text-sm font-semibold text-gray-900">{acessoSol.nomeCompleto}</p>
              <div className="pt-2 space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">Login (CPF):</span>
                  <span className="font-mono font-bold text-gray-900">{(acessoSol.cpf || '').replace(/\D/g, '')}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">Senha inicial:</span>
                  <span className="font-mono font-bold text-purple-700">{(acessoSol.cpf || '').replace(/\D/g, '').slice(0, 6)}</span>
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setAcessoSol(null)}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-2xl hover:bg-gray-50 transition">
                Cancelar
              </button>
              <button onClick={() => handleCriarAcessoAdmin(acessoSol)} disabled={criandoAcesso}
                className="flex-1 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-semibold rounded-2xl transition flex items-center justify-center gap-2">
                {criandoAcesso ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                {criandoAcesso ? 'A criar…' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Botão de Ação Reutilizável
// ─────────────────────────────────────────────────────────────────────────────
interface BtnProps {
  icon: React.ElementType
  label: string
  onClick: () => void
  color?: string
  loading?: boolean
  variant?: 'ghost' | 'filled'
  iconOnly?: boolean
}
function Btn({ icon: Icon, label, onClick, color = '#6b7280', loading = false, variant = 'ghost', iconOnly = false }: BtnProps) {
  if (variant === 'filled') {
    return (
      <button onClick={onClick} disabled={loading} title={label}
        className="flex items-center gap-1.5 px-3 py-1.5 text-white text-xs font-semibold rounded-xl transition disabled:opacity-50"
        style={{ backgroundColor: color }}>
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
        {!iconOnly && <span>{label}</span>}
      </button>
    )
  }
  return (
    <button onClick={onClick} disabled={loading} title={label}
      className="p-1.5 text-gray-400 rounded-xl transition hover:bg-gray-100 disabled:opacity-50"
      onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = color }}
      onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = '' }}>
      {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
      {!iconOnly && <span className="text-xs font-medium ml-1">{label}</span>}
    </button>
  )
}
